import { Link, useParams } from 'react-router-dom';
import { useSeo, buildBreadcrumbSchema } from '@/hooks/useSeo';
import { collections } from '@/mocks/collections';
import CollectionCard, { formatViews } from '@/components/feature/CollectionCard';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

export default function CollectionDetail() {
  const { id } = useParams<{ id: string }>();
  const collection = collections.find((item) => item.id === id);

  useSeo({
    title: collection
      ? `${collection.title} - ${collection.category}专题 | 看瓜爆料合集`
      : '专题不存在 - 看瓜爆料合集',
    description: collection
      ? `${collection.summary.slice(0, 90)}——看瓜爆料${collection.category}合集，按时间线整理完整来龙去脉。`
      : '该专题不存在或已下架，请返回看瓜爆料合集库浏览其他专题。',
    keywords: collection
      ? `${collection.title},${collection.category},看瓜爆料合集,看瓜爆料专题,${collection.tags.join(',')}`
      : '看瓜爆料合集',
    canonical: `/collections/${id ?? ''}`,
    type: 'article',
  });

  if (!collection) {
    return (
      <div className="w-full px-4 md:px-10 py-24 text-center">
        <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading font-bold text-2xl text-foreground-950">没有找到这个专题</h1>
        <p className="mt-2 text-sm text-foreground-600">它可能已被合并或改名，去合集库看看其他内容吧。</p>
        <Link
          to="/collections"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm label-font hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          返回合集库
        </Link>
      </div>
    );
  }

  const related = collections
    .filter((item) => item.id !== collection.id && item.categorySlug === collection.categorySlug)
    .slice(0, 2);

  const fallbackRelated = collections
    .filter((item) => item.id !== collection.id && item.categorySlug !== collection.categorySlug)
    .sort((a, b) => b.views - a.views)
    .slice(0, 2 - related.length);

  const relatedList = [...related, ...fallbackRelated];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: collection.title,
    description: collection.summary,
    articleSection: collection.category,
    dateModified: collection.updatedAt,
    inLanguage: 'zh-CN',
    author: { '@type': 'Organization', name: '看瓜爆料' },
    publisher: { '@type': 'Organization', name: '看瓜爆料' },
    mainEntityOfPage: `https://ijlvv.cn/collections/${collection.id}`,
    keywords: collection.tags.join(','),
  };

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: '首页', path: '/' },
    { name: '合集库', path: '/collections' },
    { name: collection.title, path: `/collections/${collection.id}` },
  ]);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-10 py-6 md:py-8">
          <Breadcrumbs
            items={[
              { name: '首页', path: '/' },
              { name: '合集库', path: '/collections' },
              { name: collection.category, path: `/collections?category=${collection.categorySlug}` },
              { name: collection.title },
            ]}
          />
        </div>
      </div>

      <article className="w-full px-4 md:px-10 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <header>
              <Link
                to={`/collections?category=${collection.categorySlug}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary-50 text-primary-700 text-xs label-font border border-primary-200 hover:bg-primary-100 transition-colors"
              >
                <i className="ri-price-tag-3-line"></i>
                {collection.category}
              </Link>
              <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-tight">
                {collection.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm label-font text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  最近更新 {collection.updatedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-git-commit-line"></i>
                  {collection.entries.length} 个节点
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(collection.views)} 次阅读
                </span>
              </div>

              <p className="mt-6 text-[15px] md:text-base text-foreground-700 leading-relaxed">
                {collection.summary}
              </p>
            </header>

            <figure className="mt-8">
              <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden border border-background-200">
                <img
                  src={collection.cover}
                  alt={`${collection.title} - ${collection.category}合集封面`}
                  title={`${collection.title} 时间线整理`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2 text-xs text-foreground-500 label-font text-center">
                图为专题氛围示意，内容以公开信息整理为主
              </figcaption>
            </figure>

            <section className="mt-12">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line"></i>
                </span>
                事件时间线
              </h2>

              <ol className="mt-6 relative border-l-2 border-background-200 ml-3">
                {collection.entries.map((entry, index) => (
                  <li key={entry.date} className="relative pl-7 pb-8 last:pb-0">
                    <span
                      className={`absolute -left-[11px] top-0 w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold label-font ring-4 ring-background-50 ${
                        index === collection.entries.length - 1
                          ? 'bg-accent-500 text-accent-950'
                          : 'bg-primary-500 text-background-50'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <time
                      dateTime={entry.date}
                      className="inline-block px-2 py-0.5 rounded-md bg-background-100 text-xs label-font text-foreground-600"
                    >
                      {entry.date}
                    </time>
                    <h3 className="mt-2 font-heading font-bold text-base md:text-lg text-foreground-950">
                      {entry.title}
                    </h3>
                    <p className="mt-2 text-sm md:text-[15px] text-foreground-600 leading-relaxed">
                      {entry.text}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            <div className="mt-10 flex flex-wrap gap-2">
              {collection.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-md bg-secondary-50 text-secondary-700 text-xs label-font border border-secondary-100"
                >
                  # {tag}
                </span>
              ))}
            </div>

            <p className="mt-8 p-4 rounded-md bg-background-100 text-xs md:text-sm text-foreground-600 leading-relaxed">
              <i className="ri-information-line mr-1 text-foreground-500"></i>
              本专题仅整理公开可查的信息并按时序呈现，不代表任何一方立场，也不构成对个体的评价。如内容有误，欢迎通过关于页面的联系方式反馈。
            </p>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-bookmark-line"></i>
                  </span>
                  专题速览
                </h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">所属分类</dt>
                    <dd className="text-foreground-900 label-font">{collection.category}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">时间线节点</dt>
                    <dd className="text-foreground-900 label-font">{collection.entries.length} 个</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">起止时间</dt>
                    <dd className="text-foreground-900 label-font">
                      {collection.entries[0].date} 起
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">最近更新</dt>
                    <dd className="text-foreground-900 label-font">{collection.updatedAt}</dd>
                  </div>
                </dl>
                <a
                  href="#newsletter-form"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm label-font hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-notification-3-line"></i>
                  订阅该专题更新
                </a>
              </div>

              <div className="mt-6">
                <h2 className="font-heading font-bold text-base text-foreground-950 mb-4">
                  相关专题推荐
                </h2>
                <div className="space-y-4">
                  {relatedList.map((item) => (
                    <CollectionCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}